import { StringInput } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { Modal } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import { TagPair, TagText } from '@dealstack/frontend/shared/components/TagPair/TagPair';
import { memo } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

import { em } from '@mantine/core';
import { useManageInvestmentWorkflowModal } from './useManageInvestmentWorkflowModal';

interface Props {
	className?: string;
	investmentWorkflowId: string;
}

const ManageInvestmentWorkflowModal = ({ className, investmentWorkflowId }: Props) => {
	const {
		createInvestmentWorkflowForm,
		onSubmit,
		shareAssociations,
		showModal,
		setShowModal,
		isLoading,
	} = useManageInvestmentWorkflowModal({ investmentWorkflowId });

	if (isLoading) {
		return <FullscreenLoader name="pageLoading" />;
	}

	return (
		<div>
			<UpdateInvestmentButton onClick={() => setShowModal(true)} variant="secondary">
				Update investment
			</UpdateInvestmentButton>
			{showModal && (
				<Modal onClose={() => setShowModal(false)} className={className}>
					<FormProvider {...createInvestmentWorkflowForm}>
						<form
							className={className}
							onSubmit={createInvestmentWorkflowForm.handleSubmit(onSubmit)}
						>
							{shareAssociations && (
								<div key={investmentWorkflowId}>
									<SectionHeader
										title="Transaction (InvestmentWorkflow)"
										subtitle={investmentWorkflowId}
									/>
									<hr />
									{shareAssociations.map((shareAssociation) => (
										<div key={shareAssociation.shareAssociationId}>
											<GridBox>
												<TagPair title="Security name">
													<TagText>
														{shareAssociation.securityName}
													</TagText>
												</TagPair>
												<TagPair title="Security type">
													<TagText>
														{shareAssociation.securityType}
													</TagText>
												</TagPair>
												<StringInput
													name={`${shareAssociation.shareAssociationId}_number`}
													label="Share Amount"
													inputType="number"
												/>
												<StringInput
													name={`${shareAssociation.shareAssociationId}_cost`}
													label="Cost"
													inputType="number"
												/>
											</GridBox>
										</div>
									))}
								</div>
							)}
							<ActionButton variant="primary" buttonType="submit">
								Submit
							</ActionButton>
						</form>
					</FormProvider>
				</Modal>
			)}
		</div>
	);
};
const UpdateInvestmentButton = styled(Button)`
	margin-bottom: 1rem;
`;
const Memoized = memo(ManageInvestmentWorkflowModal);
const GridBox = styled.div`
	display: flex;
	gap: 2rem;
	margin-bottom: 1rem;
	margin-top: 1rem;
`;
const Styled = styled(Memoized)`
	form {
		min-width: 33rem;
	}

	${TagText} {
		width: ${em(200)};
		overflow-wrap: break-word;
	}
`;
const ActionButton = styled(Button)`
	margin-top: 1rem;
	margin-left: auto;
`;

export { Styled as ManageInvestmentWorkflowModal };
